import { useState } from "react";
import { UserName } from "@/components/badges/UserName";
import {
  useReceivedMentorOffers,
  useAcceptMentorshipRequest,
  useDeclineMentorshipRequest,
  MentorshipRequest,
} from "@/hooks/useMentorshipRequests";
import { useGetOrCreateMentorshipThread } from "@/hooks/useGetMentorshipThread";
import { useAuth } from "@/lib/auth";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, Check, X, BookOpen, MessageSquare, Clock, Sparkles } from "lucide-react";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import { getStorageUrl } from "@/lib/storage";
import { toast } from "sonner";

export function ReceivedMentorOffersList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: offers, isLoading } = useReceivedMentorOffers();
  const acceptRequest = useAcceptMentorshipRequest();
  const declineRequest = useDeclineMentorshipRequest();
  const getOrCreateThread = useGetOrCreateMentorshipThread();

  const [declineDialogOpen, setDeclineDialogOpen] = useState(false);
  const [requestToDecline, setRequestToDecline] = useState<string | null>(null);

  const pendingOffers = offers?.filter((o) => o.status === "pending") || [];
  const acceptedOffers = offers?.filter((o) => o.status === "accepted") || [];
  const otherOffers = offers?.filter((o) => !["pending", "accepted"].includes(o.status)) || [];

  const handleAccept = async (request: MentorshipRequest) => {
    try {
      await acceptRequest.mutateAsync(request.id);
    } catch {
      // Error handled in mutation
    }
  };

  const handleDeclineClick = (requestId: string) => {
    setRequestToDecline(requestId);
    setDeclineDialogOpen(true);
  };

  const handleConfirmDecline = async () => {
    if (!requestToDecline) return;
    try {
      await declineRequest.mutateAsync(requestToDecline);
    } catch {
      // Error handled in mutation
    } finally {
      setDeclineDialogOpen(false);
      setRequestToDecline(null);
    }
  };

  const handleViewConversation = async (request: MentorshipRequest) => {
    try {
      let threadId = request.thread_id;
      if (!threadId) {
        threadId = await getOrCreateThread.mutateAsync(request.id);
      }
      if (!threadId) {
        toast.error("Could not open conversation - no thread available");
        return;
      }
      navigate(`/connections?threadId=${threadId}`);
    } catch (error) {
      console.error("[ReceivedMentorOffersList] handleViewConversation error:", error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!offers || offers.length === 0) {
    return null; // Don't render anything if no offers
  }

  const renderOfferCard = (request: MentorshipRequest, showActions: boolean) => (
    <Card key={request.id} className="overflow-hidden">
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <Avatar className="h-12 w-12 shrink-0">
            <AvatarImage
              src={request.mentor?.avatar_url ? getStorageUrl("avatars", request.mentor.avatar_url) : undefined}
            />
            <AvatarFallback className="bg-primary text-primary-foreground">
              {request.mentor?.full_name?.charAt(0) || "M"}
            </AvatarFallback>
          </Avatar>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="font-medium truncate">
                <UserName userId={request.mentor?.id} name={request.mentor?.full_name} fallback="Mentor" />
              </h4>
              <Badge variant="outline" className="bg-accent/10 text-accent-foreground border-accent/30">
                <Sparkles className="h-3 w-3 mr-1" />
                Mentorship Offer
              </Badge>
              {request.status === "pending" && (
                <Badge variant="outline" className="text-amber-600 border-amber-300">
                  <Clock className="h-3 w-3 mr-1" />
                  Awaiting Your Response
                </Badge>
              )}
              {request.status === "accepted" && (
                <Badge variant="outline" className="text-green-600 border-green-300">
                  <Check className="h-3 w-3 mr-1" />
                  Accepted
                </Badge>
              )}
              {request.status === "declined" && (
                <Badge variant="outline" className="text-destructive border-destructive/30">
                  Declined
                </Badge>
              )}
              {request.status === "cancelled" && (
                <Badge variant="outline" className="text-muted-foreground">
                  Cancelled
                </Badge>
              )}
            </div>

            {request.topic && (
              <div className="flex items-center gap-1 mt-1 text-sm text-muted-foreground">
                <BookOpen className="h-3 w-3" />
                {request.topic.name}
              </div>
            )}

            <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
              {request.message}
            </p>

            <p className="text-xs text-muted-foreground mt-2">
              Offered {format(new Date(request.created_at), "MMM d, yyyy")}
            </p>

            {showActions && request.status === "pending" && (
              <div className="flex gap-2 mt-3">
                <Button
                  size="sm"
                  onClick={() => handleAccept(request)}
                  disabled={acceptRequest.isPending}
                >
                  {acceptRequest.isPending ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <Check className="h-4 w-4 mr-1" />
                  )}
                  Accept
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleDeclineClick(request.id)}
                  disabled={declineRequest.isPending}
                >
                  <X className="h-4 w-4 mr-1" />
                  Decline
                </Button>
              </div>
            )}

            {request.status === "accepted" && (
              <div className="mt-3">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleViewConversation(request)}
                  disabled={getOrCreateThread.isPending}
                >
                  <MessageSquare className="h-4 w-4 mr-1" />
                  View Conversation
                </Button>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" />
          Mentorship Offers from Mentors
        </h3>
        <p className="text-sm text-muted-foreground mt-1">
          Mentors who have offered to mentor you
        </p>
      </div>

      {pendingOffers.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-muted-foreground">
            Pending ({pendingOffers.length})
          </h4>
          {pendingOffers.map((offer) => renderOfferCard(offer, true))}
        </div>
      )}

      {acceptedOffers.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-muted-foreground">
            Active ({acceptedOffers.length})
          </h4>
          {acceptedOffers.map((offer) => renderOfferCard(offer, false))}
        </div>
      )}

      {otherOffers.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-muted-foreground">
            Past ({otherOffers.length})
          </h4>
          {otherOffers.map((offer) => renderOfferCard(offer, false))}
        </div>
      )}

      {/* Decline Confirmation Dialog */}
      <AlertDialog open={declineDialogOpen} onOpenChange={setDeclineDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Decline Mentorship Offer?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to decline this mentorship offer? The mentor will be notified.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDecline}>
              Decline Offer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
